import { asyncTimeout } from "@use/async-timeout";
import { hrtime } from "@use/universal-hrtime";

import { SaveableModel } from "./SaveableModel";

/**
 * We're using this symbol to safely store the list of currently running
 * requests to create items in the collection without risking namespace
 * collisions.
 */
const createRequests = Symbol("Collection.createRequests");

/**
 * We're using this symbol to safely store the list of currently running
 * requests to delete items from the collection without risking namespace
 * collisions.
 */
const deleteRequests = Symbol("Collection.deleteRequests");

/**
 * Items within a collection needs to have at least an id, in order for the
 * collection to be able to properly manage them.
 */
export type CollectionModel = {
  readonly id: string | number;
};

/**
 * The collection interface has been designed to create classes that can easily
 * manage iterable data.
 */
// tslint:disable-next-line no-any
export abstract class Collection<T extends CollectionModel>
  implements Iterable<T>
{
  /**
   * Internal collection of items available within this collection.
   */
  protected abstract collection?: T[];

  /**
   * Internal collection of currently running create requests.
   */
  private readonly [createRequests]: Set<() => Promise<T>> = new Set();

  /**
   * Internal collection of currently running delete requests.
   */
  private readonly [deleteRequests]: Set<() => Promise<T>> = new Set();

  /**
   * Make the collection iterable with `for (const course of collection) {}`
   * patterns.
   */
  // tslint:disable-next-line function-name
  public *[Symbol.iterator](): Iterator<T> {
    if (this.collection) {
      for (const item of this.collection) {
        yield item;
      }
    }
  }

  /**
   * Helper that'll determine if there have been made any changes since the
   * last time data was saved.
   */
  public hasUnsavedChanges(): boolean {
    // If we're currently trying to create or delete items within the
    // collection, then that's considered to be unsaved changes
    if (this[createRequests].size > 0 || this[deleteRequests].size > 0) {
      return true;
    }

    // Iterate over all items in the collection, and check to see if either
    // of them has unsaved changes
    if (this.collection) {
      for (const item of this.collection) {
        if (item instanceof SaveableModel || item instanceof Collection) {
          if (item.hasUnsavedChanges()) {
            return true;
          }
        }
      }
    }

    // If we get here, there are no unsaved changes
    return false;
  }

  /**
   * Expose the sort method of the internal collection publicly.
   */
  public sort(callback: (a: T, b: T) => number): T[] {
    if (!this.collection) {
      return [];
    }

    return this.collection.slice().sort(callback);
  }

  /**
   * Expose the forEach method of the internal collection publicly.
   */
  public forEach(callback: (item: T, index: number, arr: T[]) => void): void {
    if (!this.collection) {
      return;
    }

    this.collection.forEach(callback);
  }

  /**
   * Expose the map method of the internal collection publicly.
   */
  public map<T2>(callback: (item: T, index: number, arr: T[]) => T2): T2[] {
    if (!this.collection) {
      return [];
    }

    return this.collection.map(callback);
  }

  /**
   * Expose the flatMap method of the internal collection publicly.
   */
  public flatMap<T2>(
    callback: (item: T, index: number, arr: T[]) => T2 | T2[]
  ): T2[] {
    if (!this.collection) {
      return [];
    }

    // Prepare a flat map
    const flatMap: T2[] = [];

    // Use regular map method to create a non-flattened map
    const map = this.collection.map(callback);

    // Iterate over non-flattened map and flatten it
    for (const item of map) {
      if (Array.isArray(item)) {
        // If this item wasn't flattened, then iterate over the list of
        // returned values and push them to the flattened map
        // individually
        for (const flattenedItem of item) {
          flatMap.push(flattenedItem);
        }
      } else {
        // If this was a single item, then push directly to the
        // flattened map
        flatMap.push(item);
      }
    }

    return flatMap;
  }

  /**
   * Expose the find method of the internal collection publicly.
   */
  public find(
    callback: (item: T, index: number, arr: T[]) => boolean
  ): T | undefined {
    if (!this.collection) {
      return undefined;
    }

    return this.collection.find(callback);
  }

  /**
   * Expose the findIndex method of the internal collection publicly.
   */
  public findIndex(
    callback: (item: T, index: number, arr: T[]) => boolean
  ): number {
    if (!this.collection) {
      return -1;
    }

    return this.collection.findIndex(callback);
  }

  /**
   * Expose the filter method of the internal collection publicly.
   */
  public filter(callback: (item: T, index: number, arr: T[]) => boolean): T[] {
    if (!this.collection) {
      return [];
    }

    return this.collection.filter(callback);
  }

  /**
   * Helper that'll return a reference to the item with the given id.
   */
  public getById(id: string | number): T | undefined {
    return this.find((item) => item.id === id);
  }

  /**
   * Helper that'll return a reference to the item at the given index in the
   * collection.
   */
  public getByIndex(index: number): T | undefined {
    if (!this.collection) {
      return undefined;
    }

    return this.collection[index];
  }

  /**
   * Helper that'll get the id of the item at the specified index within the
   * collection.
   */
  public getIdByIndex(index: number): string | number | undefined {
    if (
      !this.collection ||
      index < 0 ||
      index >= this.collection.length ||
      !this.collection[index]
    ) {
      return undefined;
    }

    return this.collection[index].id;
  }

  /**
   * Helper that'll get the index of the item with the specified id within
   * the collection.
   */
  public getIndexById(id: string | number): number {
    return this.findIndex((item) => item.id === id);
  }

  /**
   * Helper that'll determine if the specified item is currently added to this
   * collection.
   */
  public has(item: T): boolean {
    if (!this.collection) {
      return false;
    }

    return !!this.collection.find((findItem) => findItem === item);
  }

  /**
   * Helper that'll add the given item to the collection.
   */
  public add(item: T, index?: number): void {
    if (!this.collection) {
      this.collection = [];
    }

    if (index === undefined) {
      // If no specific index was given, then simply append the item to
      // the collection
      this.collection.push(item);
    } else {
      // Verify that the given index is within bounds
      if (index < 0 || index > this.collection.length) {
        throw new Error(
          `Collection.add(...): Index ${index} is out of bounds!`
        );
      }

      // Inject the block into the collection
      this.collection.splice(index, 0, item);
    }
  }

  /**
   * Helper that'll move the given item to the specified index.
   */
  public move(item: T, index: number): void {
    // Bail out if the collection doesn't exist
    if (!this.collection) {
      throw new Error(`Collection.move(...): Collection is empty.`);
    }

    // Identify the current index of the element
    const currentIndex = this.collection.findIndex(
      (findItem) => findItem === item
    );

    // Bail out if index hasn't actually been changed
    if (index === currentIndex) {
      return;
    }

    // Throw if we're unable to locate the desired item
    if (currentIndex === -1) {
      throw new Error(`Collection.move(...): The item wasn't found.`);
    }

    // Clone the collection so we can safely perform modifications without
    // mobx detecting multiple change events if this is an observable
    // collection
    const clonedCollection = this.collection.slice(0);

    // Then swap the position of the item to be moved and the item in the
    // position it is to be swapped with.
    const previousElement = clonedCollection[index];
    clonedCollection[index] = item;
    clonedCollection[currentIndex] = previousElement;

    // Flush changes to this.collection, allowing mobx to pick up on only a
    // single change event
    this.collection = clonedCollection;
  }

  /**
   * Helper that'll move the item with the given id to the specified index in
   * the collection.
   */
  public moveById(id: string | number, index: number): void {
    const item = this.getById(id);

    // Bail out if the item doesn't exist
    if (!item) {
      throw new Error(`Collection.moveById(...): The item wasn't found.`);
    }

    this.move(item, index);
  }

  /**
   * Helper that'll remove the given item from the collection.
   */
  public remove(item: T): void {
    if (!this.collection) {
      return;
    }

    // Now actually delete the item from the collection!
    this.collection.splice(
      this.collection.findIndex((findItem) => findItem === item),
      1
    );
  }

  /**
   * Helper that'll remove the item with the given id from the collection.
   */
  public removeById(id: string | number): void {
    const item = this.getById(id);

    // Bail out if the item doesn't exist
    if (!item) {
      return;
    }

    this.remove(item);
  }

  /**
   * Internal helper that you should always use to wrap your requests to
   * create new items made to the backend (that way the collection will
   * automatically keep state up-to-date).
   */
  protected async runCreateRequest(
    callback: () => Promise<T>,
    minDelay: number = 0
  ): Promise<T> {
    // Extract timestamp for when the request was started
    const startedAt = hrtime();

    // Register that we're performing a callback to create a new item in the
    // collection (that way we know that there are unsaved changes)
    this[createRequests].add(callback);

    try {
      // Perform the request to create the item
      const item = await callback();

      // Wait the defined minimum delay
      await asyncTimeout(Math.max(0, minDelay - (hrtime() - startedAt)));

      // ... And then actually add it to the collection
      this.add(item);

      // Return the created item publicly
      return item;
    } catch (err) {
      // Wait the defined minimum delay
      await asyncTimeout(Math.max(0, minDelay - (hrtime() - startedAt)));

      // Propagate errors to the external promise
      throw err;
    } finally {
      // And make sure to remove the request once it finishes, so we no
      // longer consider it to be active
      this[createRequests].delete(callback);
    }
  }

  /**
   * Internal helper that you should always use to wrap your requests to
   * delete items frmo the collection made to the backend (that way the
   * collection will automatically keep state up-to-date)
   */
  protected async runDeleteRequest(
    callback: () => Promise<T>,
    minDelay: number = 0,
    onBeforeRemove?: () => void
  ): Promise<void> {
    // Extract timestamp for when the request was started
    const startedAt = hrtime();

    // Register that we're performing a callback to delete an item from the
    // collection (that way we know that there are unsaved changes)
    this[deleteRequests].add(callback);

    try {
      // Perform the request to delete the item
      const item = await callback();

      // Wait the defined minimum delay
      await asyncTimeout(Math.max(0, minDelay - (hrtime() - startedAt)));

      // If an onBeforeRemove callback was given, then run it right away
      if (onBeforeRemove) {
        onBeforeRemove();
      }

      // ... And then actually remove it from the collection
      this.remove(item);
    } catch (err) {
      // Wait the defined minimum delay
      await asyncTimeout(Math.max(0, minDelay - (hrtime() - startedAt)));

      // Propagate errors to the external promise
      throw err;
    } finally {
      // And make sure to remove the request once it finishes, so we no
      // longer consider it to be active
      this[deleteRequests].delete(callback);
    }
  }

  /**
   * Exposes the size of the collection publicly.
   */
  public get size(): number {
    if (!this.collection) {
      return 0;
    }

    return this.collection.length;
  }
}
